import { useState, useEffect, useCallback } from 'react';
import { format, subDays, startOfMonth } from 'date-fns';
import {
  StarIcon,
  PuzzlePieceIcon,
  BoltIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../api/axiosConfig';
import Layout from '../components/Layout';

// ── helpers ──────────────────────────────────────────────────────────────────
const peso = (c) => (Number(c) / 100).toFixed(2);
const fmt  = (n) => Number(n).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BADGE = {
  Star:      { bg: 'bg-yellow-100 text-yellow-800 border-yellow-300', Icon: StarIcon },
  Plowhorse: { bg: 'bg-blue-100 text-blue-800 border-blue-300',       Icon: BoltIcon },
  Puzzle:    { bg: 'bg-purple-100 text-purple-800 border-purple-300', Icon: PuzzlePieceIcon },
  Dog:       { bg: 'bg-gray-100 text-gray-600 border-gray-300',       Icon: MinusCircleIcon },
};

const ACTION = {
  Star:      'Promote heavily — feature in menu and upsell. Maintain quality; resist cost-cutting.',
  Plowhorse: 'High volume but low margin. Explore cost reduction or slight price increase.',
  Puzzle:    'High margin but low volume. Improve visibility: rename, reposition, add photo.',
  Dog:       'Low volume + low margin. Consider removing or repositioning. Test a price cut.',
};

function QuadrantBadge({ quadrant }) {
  const b = BADGE[quadrant] || BADGE.Dog;
  const Icon = b.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${b.bg}`}>
      <Icon className="h-3.5 w-3.5" /> {quadrant}
    </span>
  );
}

// ── CSV export ────────────────────────────────────────────────────────────────
function exportCsv(categories, dateFrom, dateTo) {
  const rows = [['Category', 'Item', 'Qty Sold', 'Revenue (₱)', 'Food Cost (₱)', 'CM (₱)', 'Quadrant']];
  for (const cat of categories) {
    for (const item of cat.items) {
      rows.push([
        cat.category,
        item.name,
        item.totalQty,
        peso(item.totalRevenue),
        peso(item.foodCost),
        peso(item.cm),
        item.quadrant,
      ]);
    }
  }
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `menu-engineering-${dateFrom}-to-${dateTo}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── page ──────────────────────────────────────────────────────────────────────
export default function RestaurantMenuEngineeringPage() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dateTo,   setDateTo]   = useState(today);
  const [applied,  setApplied]  = useState({ from: format(startOfMonth(new Date()), 'yyyy-MM-dd'), to: today });
  const [data,      setData]      = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError,   setIsError]   = useState(false);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await api.get('/restaurant/pos-reports/menu-engineering', {
        params: { dateFrom: applied.from, dateTo: applied.to },
      });
      setData(res.data);
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [applied.from, applied.to]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  function applyFilter() {
    setApplied({ from: dateFrom, to: dateTo });
  }

  function handlePreset(days) {
    const from = format(subDays(new Date(), days), 'yyyy-MM-dd');
    setDateFrom(from);
    setDateTo(today);
    setApplied({ from, to: today });
  }

  function handlePrint() {
    window.print();
  }

  // data = res.data = { success, summary, categories, top3Profit, bot3Profit }
  const summary   = data?.summary;
  const cats      = data?.categories ?? [];
  const top3      = data?.top3Profit ?? [];
  const bot3      = data?.bot3Profit ?? [];

  // counts per quadrant across all categories
  const quadrantCounts = cats.reduce((acc, cat) => {
    for (const item of cat.items) {
      acc[item.quadrant] = (acc[item.quadrant] || 0) + 1;
    }
    return acc;
  }, {});

  return (
    <Layout pageTitle="Menu Engineering / P-Mix Report">
      {/* ── toolbar ── */}
      <div className="print-hide flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
        </div>
        <button onClick={applyFilter}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700">
          Apply
        </button>
        <div className="flex gap-2 ml-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => handlePreset(d)}
              className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs hover:bg-gray-200">
              Last {d}d
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => exportCsv(cats, applied.from, applied.to)}
            disabled={!data}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40">
            Export CSV
          </button>
          <button onClick={handlePrint}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm font-semibold hover:bg-gray-800">
            Print
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center h-48 text-gray-400">Loading report…</div>
      )}
      {!isLoading && !data && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
          No data returned. Check that orders exist for the selected period.
        </div>
      )}
      {isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          Failed to load report. Check browser console for details.
        </div>
      )}

      {data?.success && (
        <div className="space-y-6">

          {/* ── Executive Summary ── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-base font-bold text-gray-800 mb-4">Executive Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <KpiCard label="Total Revenue" value={`₱${fmt(peso(summary?.totalRevenue ?? 0))}`} />
              <KpiCard label="Total Food Cost" value={`₱${fmt(peso(summary?.totalFoodCost ?? 0))}`} sub="est." />
              <KpiCard label="Total Margin" value={`₱${fmt(peso(summary?.totalMargin ?? 0))}`} highlight />
              <KpiCard label="Items Sold" value={(summary?.totalQty ?? 0).toLocaleString()} />
            </div>

            {/* Quadrant summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {['Star', 'Plowhorse', 'Puzzle', 'Dog'].map((q) => (
                <div key={q} className={`rounded-xl border p-3 ${BADGE[q].bg}`}>
                  <div className="text-lg font-bold">{quadrantCounts[q] ?? 0}</div>
                  <div className="text-xs font-semibold flex items-center gap-1">
                    {(() => { const I = BADGE[q].Icon; return <I className="h-3.5 w-3.5" />; })()} {q}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Top 3 profit drivers */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Top 3 Profit Drivers</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase">
                      <th className="text-left pb-1">Item</th>
                      <th className="text-right pb-1">Qty</th>
                      <th className="text-right pb-1">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top3.map((item, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="py-1 font-medium">{item.name}</td>
                        <td className="text-right py-1 text-gray-600">{item.totalQty}</td>
                        <td className="text-right py-1 font-semibold text-emerald-700">₱{fmt(peso(item.totalMargin))}</td>
                      </tr>
                    ))}
                    {top3.length === 0 && <tr><td colSpan={3} className="text-gray-400 text-xs py-2">No data</td></tr>}
                  </tbody>
                </table>
              </div>

              {/* Bottom 3 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Bottom 3 by Margin</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400 uppercase">
                      <th className="text-left pb-1">Item</th>
                      <th className="text-right pb-1">Qty</th>
                      <th className="text-right pb-1">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bot3.map((item, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="py-1 font-medium">{item.name}</td>
                        <td className="text-right py-1 text-gray-600">{item.totalQty}</td>
                        <td className="text-right py-1 text-red-600">₱{fmt(peso(item.totalMargin))}</td>
                      </tr>
                    ))}
                    {bot3.length === 0 && <tr><td colSpan={3} className="text-gray-400 text-xs py-2">No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Per-category tables ── */}
          {cats.map((cat) => (
            <CategorySection key={cat.category} cat={cat} />
          ))}

          {cats.length === 0 && !isLoading && (
            <div className="text-center py-12 text-gray-400">No sales data for the selected period.</div>
          )}

          {/* ── Strategic Action Plan ── */}
          {cats.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 print-page-break">
              <h2 className="text-base font-bold text-gray-800 mb-4">Strategic Action Plan</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {['Star', 'Plowhorse', 'Puzzle', 'Dog'].map((q) => {
                  const items = cats.flatMap((c) => c.items.filter((i) => i.quadrant === q));
                  if (items.length === 0) return null;
                  return (
                    <div key={q} className={`rounded-xl border p-4 ${BADGE[q].bg}`}>
                      <div className="font-semibold text-sm mb-1 flex items-center gap-1">
                        {(() => { const I = BADGE[q].Icon; return <I className="h-4 w-4" />; })()} {q}s ({items.length})
                      </div>
                      <p className="text-xs mb-2 opacity-80">{ACTION[q]}</p>
                      <ul className="text-xs space-y-0.5">
                        {items.slice(0, 6).map((item, i) => (
                          <li key={i} className="truncate">• {item.name}</li>
                        ))}
                        {items.length > 6 && <li className="opacity-60">…and {items.length - 6} more</li>}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @media print {
          nav, aside, header, footer, .print-hide { display: none !important; }
          body { background: white !important; }
          .print-page-break { page-break-before: always; }
        }
      `}</style>
    </Layout>
  );
}

function KpiCard({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-xl p-4 ${highlight ? 'bg-indigo-50 border border-indigo-100' : 'bg-gray-50'}`}>
      <div className={`text-xl font-bold ${highlight ? 'text-indigo-700' : 'text-gray-900'}`}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label} {sub && <span className="text-gray-400">({sub})</span>}</div>
    </div>
  );
}

function CategorySection({ cat }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 print:break-inside-avoid">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-800">{cat.category}</h3>
        <span className="text-xs text-gray-400">{cat.items.length} items · thresholds: qty≥{Number(cat.qtyThreshold).toFixed(1)}, CM≥₱{Number(peso(cat.cmThreshold)).toFixed(2)}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-400 uppercase border-b border-gray-100">
              <th className="text-left pb-2 pr-3">Item</th>
              <th className="text-right pb-2 pr-3">Qty</th>
              <th className="text-right pb-2 pr-3">Revenue</th>
              <th className="text-right pb-2 pr-3">Food Cost</th>
              <th className="text-right pb-2 pr-3">CM / unit</th>
              <th className="text-right pb-2 pr-3">Total Margin</th>
              <th className="text-center pb-2">Quadrant</th>
            </tr>
          </thead>
          <tbody>
            {cat.items.map((item, i) => (
              <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                <td className="py-1.5 pr-3 font-medium">{item.name}</td>
                <td className="text-right py-1.5 pr-3 text-gray-700">{item.totalQty}</td>
                <td className="text-right py-1.5 pr-3 text-gray-700">₱{fmt(peso(item.totalRevenue))}</td>
                <td className="text-right py-1.5 pr-3 text-gray-500">₱{fmt(peso(item.foodCost))}</td>
                <td className="text-right py-1.5 pr-3 text-gray-700">₱{fmt(peso(item.cm))}</td>
                <td className={`text-right py-1.5 pr-3 font-semibold ${item.totalMargin < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                  ₱{fmt(peso(item.totalMargin))}
                </td>
                <td className="text-center py-1.5">
                  <QuadrantBadge quadrant={item.quadrant} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 font-semibold bg-gray-50">
              <td className="py-2 pr-3 text-xs text-gray-500 uppercase">Total</td>
              <td className="text-right py-2 pr-3">{cat.items.reduce((s, i) => s + i.totalQty, 0)}</td>
              <td className="text-right py-2 pr-3">₱{fmt(peso(cat.items.reduce((s, i) => s + i.totalRevenue, 0)))}</td>
              <td className="text-right py-2 pr-3">₱{fmt(peso(cat.items.reduce((s, i) => s + i.foodCost * i.totalQty, 0)))}</td>
              <td className="py-2 pr-3" />
              <td className="text-right py-2 pr-3 text-emerald-700">₱{fmt(peso(cat.items.reduce((s, i) => s + i.totalMargin, 0)))}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
